import moment from 'moment';
import { isToday, isTomorrow, endOfDay } from 'date-fns';
import type { PipelineItem } from '@/reducers/pipelineReducer';

type DueDateFields = Pick<PipelineItem, 'dueDate' | 'status'>;

// An item is overdue once its whole due day has passed; an item due today is
// "due" (amber), not late (red). Completed items are never overdue.
export const isItemOverdue = (item: DueDateFields): boolean => {
	if (!item.dueDate || item.status === 'completed') return false;
	const due = new Date(item.dueDate);
	if (Number.isNaN(due.getTime())) return false;
	return endOfDay(due) < new Date();
};

export const isItemDueToday = (item: DueDateFields): boolean => {
	if (!item.dueDate || item.status === 'completed') return false;
	const due = new Date(item.dueDate);
	if (Number.isNaN(due.getTime())) return false;
	return isToday(due);
};

export const formatDueDate = (
	date: Date | string | null | undefined
): string | null => {
	if (!date) return null;
	const d = new Date(date);
	if (Number.isNaN(d.getTime())) return null;

	if (isToday(d)) return 'Today';
	if (isTomorrow(d)) return 'Tomorrow';

	return moment(d).format('LTS');
};

export const dueDateChipClasses = (item: DueDateFields): string => {
	if (isItemOverdue(item)) {
		return 'border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400';
	}
	if (isItemDueToday(item)) {
		return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-400';
	}
	return 'border-border bg-muted/60 text-muted-foreground';
};

// ---------------------------------------------------------------------------
// Priority
// ---------------------------------------------------------------------------

export type PipelinePriority = 'low' | 'medium' | 'high' | 'urgent';

export const PRIORITY_OPTIONS: Array<{
	value: PipelinePriority;
	label: string;
}> = [
	{ value: 'low', label: 'Low' },
	{ value: 'medium', label: 'Medium' },
	{ value: 'high', label: 'High' },
	{ value: 'urgent', label: 'Urgent' },
];

// Sort order for table views (higher = more urgent).
export const PRIORITY_RANK: Record<PipelinePriority, number> = {
	low: 0,
	medium: 1,
	high: 2,
	urgent: 3,
};

const PRIORITY_CHIP_CLASSES: Partial<Record<PipelinePriority, string>> = {
	low: 'border-border bg-muted/60 text-muted-foreground',
	high: 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-400',
	urgent: 'border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400',
};

// Medium is the default on every item, so it renders no chip — only
// deliberate priority choices show up on cards.
export const getPriorityChip = (
	priority: PipelinePriority | undefined
): { label: string; classes: string } | null => {
	if (!priority || priority === 'medium') return null;
	const classes = PRIORITY_CHIP_CLASSES[priority];
	if (!classes) return null;
	return {
		label: priority.charAt(0).toUpperCase() + priority.slice(1),
		classes,
	};
};

// ---------------------------------------------------------------------------
// Aging (time in current stage)
// ---------------------------------------------------------------------------

export const AGING_WARN_DAYS = 7;
export const AGING_ALERT_DAYS = 14;

type AgingFields = Pick<PipelineItem, 'movedAt' | 'createdAt' | 'status'>;

// Whole days the item has sat in its current column. Null when the item is
// completed or has no usable timestamps.
export const getDaysInStage = (item: AgingFields): number | null => {
	if (item.status === 'completed') return null;
	const since = item.movedAt || item.createdAt;
	if (!since) return null;
	const start = new Date(since);
	if (Number.isNaN(start.getTime())) return null;
	const days = Math.floor((Date.now() - start.getTime()) / 86400000);
	return days > 0 ? days : 0;
};

export const agingChipClasses = (days: number): string => {
	if (days >= AGING_ALERT_DAYS) {
		return 'border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400';
	}
	if (days >= AGING_WARN_DAYS) {
		return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-400';
	}
	return 'border-border bg-muted/60 text-muted-foreground';
};
